import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FILE_URL } from "@/lib/api";
import {
  Gift,
  Link as LinkIcon,
  Lightbulb,
  Check,
  ShoppingBag,
  X,
  Info,
  AlertTriangle,
  Pencil,
  Users,
  Package,
  Repeat,
  Ticket,
  Wrench,
  Palette,
  HeartHandshake,
  Tag,
} from "lucide-react";
import { toast } from "sonner";
import { LinkPreviewThumb } from "@/components/LinkPreviewThumb";

// Same enum as GiftListDetail — kept as a small module-local map so the
// public shared view can render a matching category chip without needing to
// import the owner-side file (avoids pulling in owner-only bits).
const ITEM_CATEGORY_BADGES = {
  product:      { label: "Product",       icon: Package },
  subscription: { label: "Subscription",  icon: Repeat },
  experience:   { label: "Experience",    icon: Ticket },
  service:      { label: "Service",       icon: Wrench },
  handmade:     { label: "Handmade",      icon: Palette },
  donation:     { label: "Donation",      icon: HeartHandshake },
};

// Merges the built-in badges above with the list's owner-defined custom
// categories (which arrive from the API as `[{slug, label}]`). Custom
// entries get a neutral Tag icon so the shared view can render them
// consistently in category chips + group headers.
function buildBadges(customCategories = []) {
  const merged = { ...ITEM_CATEGORY_BADGES };
  for (const c of customCategories || []) {
    if (c?.slug && c?.label) {
      merged[c.slug] = { label: c.label, icon: Tag };
    }
  }
  return merged;
}

const VIEWER_KEY = "willoah.giftviewer.v1";

function getViewerId() {
  try {
    let id = localStorage.getItem(VIEWER_KEY);
    if (!id) {
      // Not a strict UUID — a collision-safe local viewer id (random hex + time)
      const rand = () =>
        Math.random().toString(16).slice(2).padEnd(13, "0") +
        Date.now().toString(16);
      id = `v_${rand()}`.slice(0, 32);
      localStorage.setItem(VIEWER_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

// Canonicalize a URL for duplicate detection: host + pathname (case-insensitive,
// trailing slash stripped). Ignores query strings so tracking params don't hide
// duplicates. Falls back to the raw string if URL parsing fails.
function canonicalUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.host.toLowerCase().replace(/^www\./, "");
    const path = u.pathname.replace(/\/+$/, "");
    return `${host}${path}`;
  } catch {
    return url.trim().toLowerCase() || null;
  }
}

// Extract a numeric amount from a free-form string like "$400", "€40.50",
// "40 kr", "1,200", or "half". Returns { amount, symbol } or null when not
// parseable. Uses the first currency symbol/letter run for display.
function parseAmount(s) {
  if (!s) return null;
  const str = String(s).trim();
  const num = str.match(/(\d[\d,]*\.?\d*)/);
  if (!num) return null;
  const amount = parseFloat(num[1].replace(/,/g, ""));
  if (!Number.isFinite(amount) || amount <= 0) return null;
  // Symbol: everything except the first number match, trimmed
  const symMatch = str.match(/[^\d.,\s]+/);
  return { amount, symbol: symMatch ? symMatch[0] : "" };
}

function fmt(amount, symbol) {
  const rounded = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
  const parts = rounded.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const num = parts.join(".");
  if (!symbol) return num;
  // Prefix if it starts with a letter/currency symbol placed BEFORE the number in the input
  return `${symbol}${num}`;
}

export default function SharedGiftList() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState({}); // item_id -> boolean

  const viewerId = useMemo(getViewerId, []);
  const client = useMemo(() => {
    return axios.create({
      baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
      headers: { "X-Willoah-Viewer": viewerId },
      withCredentials: true,
    });
  }, [viewerId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await client.get(`/shared-gift-list/${token}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.status === 404 ? "This share link isn't active." : "Couldn't load the list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const claim = async (itemId, action = "claimed", viewerName, viewerNote, shareAmount) => {
    setPending((p) => ({ ...p, [itemId]: true }));
    try {
      await client.post(`/shared-gift-list/${token}/items/${itemId}/claim`, {
        action,
        viewer_name: viewerName || null,
        viewer_note: viewerNote || null,
        viewer_share_amount: shareAmount || null,
      });
      await load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't claim this item.");
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[itemId];
        return next;
      });
    }
  };

  const unclaim = async (itemId) => {
    setPending((p) => ({ ...p, [itemId]: true }));
    try {
      await client.delete(`/shared-gift-list/${token}/items/${itemId}/claim`);
      await load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't undo the claim.");
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[itemId];
        return next;
      });
    }
  };

  if (loading) {
    return <main className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-[#2A2823]/60 text-sm">Loading the list…</main>;
  }
  if (error) {
    return <main className="max-w-3xl mx-auto px-6 pt-16 pb-24 text-[#2A2823]/70">{error}</main>;
  }
  if (!data) return null;

  const { list, owner_name, items, owner_view } = data;
  // Merge preset + list-scoped custom category badges so both the group
  // headers and the row-level chip can render owner-defined names.
  const badges = buildBadges(list?.custom_categories);

  // Build a canonical-URL -> item count map so we can flag duplicates.
  // Only meaningful for viewers (owner_view items don't need this — but harmless
  // to compute anyway; we only render the chip below when !ownerView).
  const dupCount = {};
  for (const it of items) {
    const c = canonicalUrl(it.link);
    if (!c) continue;
    dupCount[c] = (dupCount[c] || 0) + 1;
  }

  return (
    <main className="max-w-3xl mx-auto px-6 pt-10 pb-24" data-testid="shared-gift-list">
      {list.cover_photo_path && (
        <div className="relative rounded-3xl overflow-hidden border border-[#B79256]/25 mb-6" data-testid="shared-cover">
          <div className="aspect-[3/1]">
            <img
              src={FILE_URL(list.cover_photo_path)}
              alt=""
              className="w-full h-full object-cover"
              draggable={false}
            />
          </div>
        </div>
      )}
      <div className="flex items-start gap-3">
        <Gift className="w-6 h-6 text-[#B79256] mt-3" strokeWidth={1.75} />
        <div className="min-w-0">
          <span className="accent-label text-[#B79256]">{owner_name}'s gift list</span>
          <h1
            data-testid="shared-list-title"
            className="font-serif-display text-5xl text-[#2A2823] mt-2 leading-none truncate"
          >
            {list.name}
          </h1>
        </div>
      </div>

      {owner_view ? (
        <div className="mt-6 rounded-2xl border border-[#B79256]/30 bg-[#B79256]/8 px-5 py-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-[#B79256] mt-0.5 shrink-0" strokeWidth={1.75} />
          <p className="text-sm text-[#2A2823]/75">
            You're viewing your own list — the surprise is preserved, so no claim information is shown here.
            Manage your list from Gift Lists in your profile menu.
          </p>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-[#B79256]/30 bg-[#B79256]/8 px-5 py-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-[#B79256] mt-0.5 shrink-0" strokeWidth={1.75} />
          <p className="text-sm text-[#2A2823]/75">
            Mark an item to reserve it so no one buys the same thing. {owner_name} will never see which
            items are claimed.
          </p>
        </div>
      )}

      {/* Category header groups + jump-nav — only when the list is long
          enough AND has 2+ different categories represented, otherwise the
          headers add visual noise. Categories that have zero items are
          omitted so friends aren't sent to empty anchors. */}
      {(() => {
        const buckets = new Map();
        for (const it of items) {
          const c = it.item_category || "product";
          if (!buckets.has(c)) buckets.set(c, []);
          buckets.get(c).push(it);
        }
        const orderedIds = [
          "product", "subscription", "experience", "service", "handmade", "donation",
          // Owner-defined custom categories appear after presets, in the
          // order the owner added them.
          ...(list?.custom_categories || []).map((c) => c.slug),
        ];
        const activeGroups = orderedIds.filter((k) => buckets.has(k));
        const showGrouped = activeGroups.length >= 2 && items.length >= 4;
        if (!showGrouped) {
          return (
            <div className="mt-10 space-y-3" data-testid="shared-items">
              {items.length === 0 && (
                <p className="text-[#2A2823]/60 text-sm">This list is empty right now.</p>
              )}
              {items.map((it) => {
                const canon = canonicalUrl(it.link);
                const isDuplicate = !owner_view && canon && dupCount[canon] > 1;
                return (
                  <SharedItemRow
                    key={it.id}
                    item={it}
                    ownerView={owner_view}
                    pending={!!pending[it.id]}
                    isDuplicate={isDuplicate}
                    onClaim={(action, name, note) => claim(it.id, action, name, note)}
                    onUnclaim={() => unclaim(it.id)}
                    myViewerId={viewerId}
                    badges={badges}
                  />
                );
              })}
            </div>
          );
        }
        return (
          <div className="mt-10" data-testid="shared-items">
            {/* Jump nav */}
            <div
              data-testid="shared-jump-nav"
              className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-[#B79256]/20"
            >
              <span className="accent-label text-[10px] text-[#B79256] mr-1">jump to</span>
              {activeGroups.map((k) => {
                const cat = badges[k] || badges.product;
                const Icon = cat.icon;
                return (
                  <a
                    key={k}
                    href={`#cat-${k}`}
                    data-testid={`shared-jump-${k}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#B79256]/40 bg-transparent text-[#2A2823] hover:bg-[#B79256]/10 px-3 py-1 text-xs transition-colors"
                  >
                    <Icon className="w-3 h-3" strokeWidth={2} />
                    {cat.label}
                    <span className="text-[#2A2823]/50">· {buckets.get(k).length}</span>
                  </a>
                );
              })}
            </div>
            {activeGroups.map((k) => {
              const cat = badges[k] || badges.product;
              const Icon = cat.icon;
              const group = buckets.get(k);
              return (
                <section
                  key={k}
                  id={`cat-${k}`}
                  data-testid={`shared-group-${k}`}
                  className="mb-10 scroll-mt-20"
                >
                  <h2 className="font-serif-display text-2xl text-[#2A2823] flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5 text-[#4E5A3D]" strokeWidth={1.75} />
                    {cat.label}
                    <span className="accent-label text-[10px] text-[#B79256] font-normal">
                      · {group.length}
                    </span>
                  </h2>
                  <div className="space-y-3">
                    {group.map((it) => {
                      const canon = canonicalUrl(it.link);
                      const isDuplicate = !owner_view && canon && dupCount[canon] > 1;
                      return (
                        <SharedItemRow
                          key={it.id}
                          item={it}
                          ownerView={owner_view}
                          pending={!!pending[it.id]}
                          isDuplicate={isDuplicate}
                          onClaim={(action, name, note) => claim(it.id, action, name, note)}
                          onUnclaim={() => unclaim(it.id)}
                          myViewerId={viewerId}
                          badges={badges}
                        />
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        );
      })()}
    </main>
  );
}

function SharedItemRow({ item, ownerView, pending, isDuplicate, onClaim, onUnclaim, badges = ITEM_CATEGORY_BADGES }) {
  const [expandForm, setExpandForm] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [shareAmount, setShareAmount] = useState("");
  const [wantGroup, setWantGroup] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [editNoteText, setEditNoteText] = useState("");
  const [editShareText, setEditShareText] = useState("");

  const isSpecific = item.kind === "specific";
  const claims = item.claims || [];
  const isGroup = !ownerView && item.is_group;
  const soloTaken = !ownerView && claims.some((c) => c.action !== "co_claim" && !c.is_me);
  const isMine = !ownerView && item.claimed_by_me;
  const myClaim = claims.find((c) => c.is_me);

  const canSoloClaim = !ownerView && !isMine && claims.length === 0;
  const canJoinGroup = !ownerView && !isMine && isGroup;

  const beginEditNote = () => {
    setEditNoteText(myClaim?.viewer_note || "");
    setEditShareText(myClaim?.viewer_share_amount || "");
    setEditingNote(true);
  };
  const saveEditNote = () => {
    onClaim(
      myClaim?.action || "claimed",
      myClaim?.viewer_name || null,
      editNoteText.trim() || null,
      editShareText.trim() || null,
    );
    setEditingNote(false);
  };

  return (
    <div
      data-testid={`shared-item-${item.id}`}
      className={`rounded-2xl border p-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4 transition-colors ${
        soloTaken
          ? "border-[#B79256]/20 bg-[#B79256]/6 opacity-75"
          : isMine
            ? "border-[#4E5A3D]/40 bg-[#4E5A3D]/6"
            : isGroup
              ? "border-[#B79256]/40 bg-[#B79256]/5"
              : "border-[#B79256]/25 bg-white/60"
      }`}
    >
      {isSpecific ? (
        <LinkPreviewThumb
          src={item.preview_image_url}
          overridePath={item.preview_image_override_path}
          alt={item.title}
          size="md"
          testId={`shared-gift-item-preview-${item.id}`}
        />
      ) : item.preview_image_override_path ? (
        <LinkPreviewThumb
          src={null}
          overridePath={item.preview_image_override_path}
          alt={item.title}
          size="md"
          testId={`shared-gift-item-photo-${item.id}`}
        />
      ) : (
        <span
          className="shrink-0 w-16 h-16 rounded-lg bg-[#B79256]/15 text-[#B79256] flex items-center justify-center"
          aria-hidden="true"
          title="Idea only"
        >
          <Lightbulb className="w-5 h-5" strokeWidth={1.75} />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className={`font-serif text-lg ${soloTaken ? "line-through text-[#2A2823]/50" : "text-[#2A2823]"}`}
          >
            {item.title}
          </span>
          {(() => {
            const cat = badges[item.item_category] || badges.product;
            const Icon = cat.icon;
            return (
              <span
                data-testid={`shared-item-category-${item.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-[#B79256]/10 border border-[#B79256]/30 text-[#8B6E3F] px-2 py-0.5 text-[10px]"
              >
                <Icon className="w-2.5 h-2.5" strokeWidth={2} />
                {cat.label}
              </span>
            );
          })()}
          {item.price_hint && (
            <span className="text-xs text-[#2A2823]/50">{item.price_hint}</span>
          )}
          {soloTaken && (
            <span data-testid={`item-taken-${item.id}`} className="accent-label text-[#B79256]">
              {claims[0]?.action === "purchased" ? "purchased" : "claimed"}
              {claims[0]?.viewer_name ? ` by ${claims[0].viewer_name}` : ""}
            </span>
          )}
          {isGroup && (
            <span data-testid={`item-group-${item.id}`} className="accent-label text-[#B79256] inline-flex items-center gap-1">
              <Users className="w-3 h-3" strokeWidth={2} />
              group gift · {claims.length}
            </span>
          )}
          {isMine && !isGroup && (
            <span data-testid={`item-mine-${item.id}`} className="accent-label text-[#4E5A3D]">
              you have this one
            </span>
          )}
        </div>
        {item.store_name && isSpecific && (
          <div
            data-testid={`shared-gift-item-store-${item.id}`}
            className="accent-label text-[10px] text-[#2A2823]/60 mt-0.5"
          >
            {item.store_name}
          </div>
        )}
        {item.notes && (
          <p className="text-sm text-[#2A2823]/70 mt-1 whitespace-pre-wrap">{item.notes}</p>
        )}
        {item.link && isSpecific && (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] hover:underline"
          >
            <LinkIcon className="w-3 h-3" strokeWidth={1.75} />
            Open link
          </a>
        )}

        {/* Duplicate warning — only viewer-side */}
        {isDuplicate && (
          <div
            data-testid={`dup-warning-${item.id}`}
            className="mt-3 inline-flex items-start gap-2 rounded-2xl border border-[#B79256]/40 bg-[#B79256]/10 px-3 py-2 text-xs text-[#7A5A2E]"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2} />
            <span>
              Heads up — another item on this list points to the same store. Check with the other
              viewer before buying so you don't split a gift.
            </span>
          </div>
        )}

        {/* Contributor list for group gifting (viewers only, not owner) */}
        {!ownerView && isGroup && claims.length > 0 && (
          <div
            data-testid={`contributors-${item.id}`}
            className="mt-3 rounded-2xl bg-[#B79256]/8 border border-[#B79256]/30 px-3 py-2 space-y-1.5"
          >
            <div className="accent-label text-[#B79256]">contributors</div>
            {claims.map((c, idx) => (
              <div
                key={idx}
                className="text-sm text-[#2A2823] flex items-baseline gap-2 flex-wrap"
              >
                <span className={c.is_me ? "font-semibold text-[#4E5A3D]" : ""}>
                  {c.is_me ? "You" : (c.viewer_name || "Someone")}
                </span>
                {c.viewer_share_amount && (
                  <span className="text-xs text-[#7A5A2E] bg-[#B79256]/15 rounded-full px-2 py-0.5">
                    {c.viewer_share_amount}
                  </span>
                )}
                {c.viewer_note && (
                  <span className="text-xs text-[#2A2823]/70 italic">— {c.viewer_note}</span>
                )}
              </div>
            ))}
            {(() => {
              // Chip-in total — sum parseable share amounts, compare to item price hint
              const parts = claims
                .map((c) => parseAmount(c.viewer_share_amount))
                .filter(Boolean);
              if (parts.length === 0) return null;
              const symbol = parts[0].symbol;
              const total = parts.reduce((s, p) => s + p.amount, 0);
              const goal = parseAmount(item.price_hint);
              if (goal) {
                const pct = Math.min(100, Math.round((total / goal.amount) * 100));
                const covered = total >= goal.amount;
                return (
                  <div className="pt-2" data-testid={`chip-in-${item.id}`}>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="text-[#7A5A2E]">
                        <b>{fmt(total, symbol || goal.symbol)}</b> of {fmt(goal.amount, goal.symbol)} pledged
                      </span>
                      <span className={covered ? "text-[#4E5A3D] accent-label" : "text-[#2A2823]/50"}>
                        {covered ? "fully covered" : `${pct}%`}
                      </span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-[#B79256]/20 overflow-hidden">
                      <div
                        className={`h-full transition-all ${covered ? "bg-[#4E5A3D]" : "bg-[#B79256]"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              }
              // No price hint — just show total pledged
              return (
                <div className="pt-2 text-xs text-[#7A5A2E]" data-testid={`chip-in-${item.id}`}>
                  <b>{fmt(total, symbol)}</b> pledged so far
                </div>
              );
            })()}
          </div>
        )}

        {/* Solo claim note (single claimer, not group) — visible to other viewers */}
        {!ownerView && !isGroup && item.is_claimed && claims[0]?.viewer_note && !isMine && (
          <div
            data-testid={`claim-note-${item.id}`}
            className="mt-3 rounded-2xl bg-[#4E5A3D]/8 border border-[#4E5A3D]/25 px-3 py-2 text-sm text-[#4E5A3D]"
          >
            <span className="accent-label text-[#4E5A3D] mr-2">note</span>
            <span className="whitespace-pre-wrap">{claims[0].viewer_note}</span>
          </div>
        )}

        {/* Own note (solo) — display + inline edit */}
        {isMine && !isGroup && !editingNote && (
          <div
            data-testid={`my-note-${item.id}`}
            className="mt-3 rounded-2xl bg-[#4E5A3D]/10 border border-[#4E5A3D]/30 px-3 py-2 text-sm text-[#4E5A3D] flex items-start gap-2"
          >
            <span className="accent-label text-[#4E5A3D] mr-1">your note</span>
            <span className="whitespace-pre-wrap flex-1">
              {myClaim?.viewer_note || <span className="text-[#4E5A3D]/60">No note yet.</span>}
            </span>
            <button
              type="button"
              data-testid={`edit-note-${item.id}`}
              onClick={beginEditNote}
              className="p-1 rounded-full text-[#4E5A3D]/70 hover:text-[#4E5A3D] hover:bg-[#4E5A3D]/10 transition-colors"
              aria-label="Edit your note"
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        )}

        {/* Own edit affordance in group mode (edit your share + note) */}
        {isMine && isGroup && !editingNote && (
          <button
            type="button"
            data-testid={`edit-note-${item.id}`}
            onClick={beginEditNote}
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#4E5A3D] hover:underline"
          >
            <Pencil className="w-3 h-3" strokeWidth={2} />
            Edit your share
          </button>
        )}

        {isMine && editingNote && (
          <div className="mt-3 space-y-2" data-testid={`edit-note-form-${item.id}`}>
            {isGroup && (
              <Input
                data-testid={`edit-share-input-${item.id}`}
                value={editShareText}
                onChange={(e) => setEditShareText(e.target.value)}
                placeholder="Your share (e.g. $40 or half)"
                className="h-9 rounded-full border-[#4E5A3D]/30 bg-[#FBF8F1] text-sm px-4"
                maxLength={40}
              />
            )}
            <Textarea
              data-testid={`edit-note-input-${item.id}`}
              value={editNoteText}
              onChange={(e) => setEditNoteText(e.target.value)}
              placeholder="e.g. 'I'm getting the blue one, in medium' — only other viewers see this."
              className="rounded-2xl border-[#4E5A3D]/30 bg-[#FBF8F1] focus-visible:ring-[#4E5A3D] focus-visible:border-[#4E5A3D] px-4 text-sm"
              maxLength={300}
            />
            <div className="flex justify-end gap-2">
              <Button
                data-testid={`edit-note-cancel-${item.id}`}
                onClick={() => setEditingNote(false)}
                className="rounded-full bg-white text-[#2A2823] hover:bg-[#B79256]/10 border border-[#B79256]/40 h-8 px-3 shadow-none text-xs"
              >
                Cancel
              </Button>
              <Button
                data-testid={`edit-note-save-${item.id}`}
                onClick={saveEditNote}
                disabled={pending}
                className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-8 px-3 shadow-none text-xs"
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Actions (viewer only) */}
      {!ownerView && (
        <div className="flex-shrink-0 flex flex-col items-end gap-2" data-testid={`item-actions-${item.id}`}>
          {isMine ? (
            <Button
              data-testid={`unclaim-${item.id}`}
              onClick={onUnclaim}
              disabled={pending}
              className="rounded-full bg-white text-[#2A2823] hover:bg-[#B79256]/10 border border-[#B79256]/40 h-9 px-4 shadow-none"
            >
              <X className="w-4 h-4 mr-2" strokeWidth={1.75} />
              {isGroup ? "Leave" : "Undo"}
            </Button>
          ) : soloTaken ? (
            <span className="text-xs text-[#2A2823]/50 pr-1">Someone's on it</span>
          ) : expandForm ? (
            <div className="flex flex-col gap-2 w-full sm:w-72" data-testid={`claim-form-${item.id}`}>
              <Input
                data-testid={`name-input-${item.id}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="h-9 rounded-full border-[#B79256]/40 bg-[#FBF8F1] text-sm px-4"
                maxLength={60}
              />
              {/* Group toggle — only if solo-claim is still possible */}
              {canSoloClaim && (
                <label className="flex items-center gap-2 text-xs text-[#2A2823]/70 px-2">
                  <input
                    type="checkbox"
                    data-testid={`want-group-${item.id}`}
                    checked={wantGroup}
                    onChange={(e) => setWantGroup(e.target.checked)}
                    className="accent-[#4E5A3D]"
                  />
                  Split this with others (group gift)
                </label>
              )}
              {(canJoinGroup || wantGroup) && (
                <Input
                  data-testid={`share-input-${item.id}`}
                  value={shareAmount}
                  onChange={(e) => setShareAmount(e.target.value)}
                  placeholder="Your share (e.g. $40 or half)"
                  className="h-9 rounded-full border-[#B79256]/40 bg-[#FBF8F1] text-sm px-4"
                  maxLength={40}
                />
              )}
              <Textarea
                data-testid={`note-input-${item.id}`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="A short note for other viewers (optional)"
                className="rounded-2xl border-[#B79256]/40 bg-[#FBF8F1] text-sm px-4 min-h-[70px]"
                maxLength={300}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`cancel-claim-${item.id}`}
                  onClick={() => {
                    setExpandForm(false); setName(""); setNote(""); setShareAmount(""); setWantGroup(false);
                  }}
                  className="text-xs text-[#2A2823]/60 hover:text-[#2A2823] transition-colors px-2"
                >
                  Cancel
                </button>
                <Button
                  data-testid={`confirm-claim-${item.id}`}
                  onClick={() => {
                    const action = canJoinGroup || wantGroup ? "co_claim" : "claimed";
                    onClaim(action, name.trim() || null, note.trim() || null, shareAmount.trim() || null);
                    setExpandForm(false);
                    setName("");
                    setNote("");
                    setShareAmount("");
                    setWantGroup(false);
                  }}
                  disabled={pending}
                  className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-8 px-4 shadow-none text-xs"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />
                  {canJoinGroup || wantGroup ? "Join group" : "Confirm"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button
                data-testid={`claim-${item.id}`}
                onClick={() => setExpandForm(true)}
                disabled={pending}
                className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 shadow-none text-sm"
              >
                {canJoinGroup ? (
                  <>
                    <Users className="w-4 h-4 mr-2" strokeWidth={2} />
                    Join the group
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" strokeWidth={2} />
                    I'll get this
                  </>
                )}
              </Button>
              {canSoloClaim && isSpecific && item.link && (
                <Button
                  data-testid={`purchased-${item.id}`}
                  onClick={() => onClaim("purchased", null, null, null)}
                  disabled={pending}
                  className="rounded-full bg-white text-[#2A2823] hover:bg-[#B79256]/10 border border-[#B79256]/40 h-9 px-4 shadow-none text-sm"
                  title="Mark as purchased"
                >
                  <ShoppingBag className="w-4 h-4" strokeWidth={1.75} />
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
